import { formatRelative, format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { enUS } from 'date-fns/locale';
import padStart from 'lodash/padStart';

export const parseDate = (date: string | Date | number): Date => {
  if (date instanceof Date) return date;

  return new Date(date);
};

export function formatPeriod(p?: string | null) {
  if (!p) {
    return '';
  }

  if (p === 'monthly') {
    return 'per month';
  }
  if (p === 'yearly') {
    return 'per year';
  }
  return p;
}

export const formatDate = (date: Date | string | number, hideActualYear?: boolean): string => {
  const actualYear = new Date().getFullYear();
  const parsedDate = parseDate(date);
  if (hideActualYear && parsedDate.getFullYear() === actualYear) {
    return format(parsedDate, 'MMM d');
  }
  return format(parsedDate, 'MMM d, y');
};

type FormatDateTimeOptions = {
  hideSeconds?: boolean;
  hideYear?: boolean;
  timeZone?: string;
};

export const formatDateTime = (date: Date | string, options?: FormatDateTimeOptions): string => {
  const parsedDate = parseDate(date);
  let dateFormat = 'MMM d, y h:mm:ssaaa';

  if (!options) return format(parsedDate, dateFormat);

  if (options.hideSeconds) {
    dateFormat = dateFormat.replace(':ss', '');
  }

  const actualYear = new Date().getFullYear();
  if (options.hideYear && parsedDate.getFullYear() === actualYear) {
    dateFormat = dateFormat.replace('y ', '');
  }

  if (options.timeZone) {
    return formatInTimeZone(parsedDate, options.timeZone, dateFormat);
  }

  return format(parsedDate, dateFormat);
};

export const formatTime = (date: Date | string, options?: FormatDateTimeOptions): string => {
  const parsedDate = parseDate(date);
  let dateFormat = 'h:mm:ssaaa';

  if (options?.hideSeconds) {
    dateFormat = dateFormat.replace(':ss', '');
  }

  return format(parsedDate, dateFormat);
};

export const formatDateTimeToInput = (date: Date | string): string => {
  const parsedDate = parseDate(date);
  return format(parsedDate, "yyyy-MM-dd'T'HH:mm");
};

type FormatArgs = number | { decimalPlaces: number; hideZeroFractional?: boolean };
export const formatNumber = (number: number, decimalPlacesOrOpts: FormatArgs = 0) => {
  const decimalPlaces =
    typeof decimalPlacesOrOpts === 'number' ? decimalPlacesOrOpts : decimalPlacesOrOpts.decimalPlaces || 0;
  const trailingZeroDisplay =
    typeof decimalPlacesOrOpts === 'object' && decimalPlacesOrOpts.hideZeroFractional ? 'stripIfInteger' : 'auto';
  return number.toLocaleString('en', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    // @ts-expect-error 97% supported but not in TS for some reason
    trailingZeroDisplay,
  });
};
export const formatMoney = (number: number, decimalPlacesOrOpts: FormatArgs = 0) =>
  `${formatNumber(number, decimalPlacesOrOpts)} USD`;

export const formatDuration = (duration: number, zeroPaddingCount = 2): string => {
  const parts = [];
  let time = duration * 1000;
  const milliseconds = Math.round(time % 1000);
  time -= milliseconds;
  time /= 1000;
  const seconds = Math.round(time % 60);
  time -= seconds;
  time /= 60;
  const minutes = Math.round(time % 60);
  time -= minutes;
  time /= 60;
  const hours = Math.round(time % 24);
  time -= hours;
  time /= 24;
  const days = Math.round(time);

  if (days > 0) {
    parts.push(`${padStart(String(days), zeroPaddingCount, '0')}d`);
  }
  if (hours > 0) {
    parts.push(`${padStart(String(hours), zeroPaddingCount, '0')}h`);
  }
  if (minutes > 0) {
    parts.push(`${padStart(String(minutes), zeroPaddingCount, '0')}m`);
  }

  if (seconds > 0 || minutes > 0) {
    let secondsWithMilliseconds = seconds;
    if (milliseconds > 0) {
      secondsWithMilliseconds += Math.round(milliseconds / 100) / 10;
    }
    parts.push(`${padStart(String(secondsWithMilliseconds || 0), zeroPaddingCount, '0')}s`);
  }

  if (milliseconds > 0 && parts.length === 0) {
    parts.push(`${padStart(String(milliseconds || 0), zeroPaddingCount, '0')}ms`);
  }

  return parts.slice(0, 2).join(' ') || '0s';
};

export const formatPercent = (value: number): string => {
  return `${Math.trunc(value * 100)}%`;
};

const FormatRelativeTokenMap = {
  lastWeek: "'Last' eee",
  yesterday: "'Yesterday'",
  today: "'Today'",
  tomorrow: "'Tomorrow'",
  nextWeek: "'Next' eee",
  other: 'MMM do, yyyy',
};

export const formatRelativeCustom = (date: number | Date, baseDate: number | Date) =>
  formatRelative(date, baseDate, {
    weekStartsOn: 1,
    locale: {
      ...enUS,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      formatRelative: (token) => FormatRelativeTokenMap[token],
    },
  });
